import { useCallback, useEffect, useRef, useState } from 'react';
import AiVoiceClient, { AiVoiceWorkflow } from '../apiClients/AiVoiceClient';

export type LiveAiVoiceState =
    | 'idle'
    | 'requesting-permission'
    | 'connecting'
    | 'listening'
    | 'thinking'
    | 'speaking'
    | 'muted'
    | 'reconnecting'
    | 'ended'
    | 'error'
    | 'unavailable';

export interface LiveAiVoiceOptions {
    workflow: AiVoiceWorkflow;
    contextId?: string | null;
    disabled?: boolean;
    onUserTranscript: (transcript: string, voiceSessionId: string) => Promise<string | null | undefined>;
    onFallbackToText?: () => void;
}

export interface LiveAiVoiceController {
    state: LiveAiVoiceState;
    isMuted: boolean;
    isMicrophoneActive: boolean;
    userCaption: string;
    assistantCaption: string;
    error: string;
    start: () => Promise<void>;
    toggleMute: () => void;
    end: () => void;
    reconnect: () => Promise<void>;
    fallbackToText: () => void;
}

type RealtimeEvent = {
    type?: string;
    item_id?: string;
    item?: { id?: string };
    transcript?: string;
    delta?: string;
};

const activeStates: LiveAiVoiceState[] = ['requesting-permission', 'connecting', 'listening', 'thinking', 'speaking', 'muted', 'reconnecting'];
const maxVoiceSessionMilliseconds = 55 * 60 * 1000;

const pick = (source: any, pascalName: string, fallback?: any) => {
    if (source === null || source === undefined)
        return fallback;

    const camelName = pascalName.charAt(0).toLowerCase() + pascalName.slice(1);
    return source[pascalName] ?? source[camelName] ?? fallback;
};

export const normalizeVoiceSession = (value: unknown) => {
    const source = value && typeof value === 'object' ? value as any : {};
    return {
        available: Boolean(pick(source, 'Available', false)),
        sessionId: pick(source, 'SessionId', null) as string | null,
        clientSecret: pick(source, 'ClientSecret', null) as string | null,
        expiresAt: pick(source, 'ExpiresAt', null) as number | null,
        callsUrl: pick(source, 'CallsUrl', null) as string | null,
        unavailableReason: pick(source, 'UnavailableReason', null) as string | null,
    };
};

const describeError = (error: unknown) => {
    if (error instanceof DOMException && (error.name === 'NotAllowedError' || error.name === 'SecurityError'))
        return 'Microphone permission was denied. Allow microphone access or continue with text chat.';
    if (error instanceof DOMException && error.name === 'NotFoundError')
        return 'No microphone was found. Connect a microphone or continue with text chat.';
    return 'The live voice conversation disconnected. You can reconnect or continue with text chat.';
};

export const useLiveAiVoice = ({
    workflow,
    contextId,
    disabled = false,
    onUserTranscript,
    onFallbackToText,
}: LiveAiVoiceOptions): LiveAiVoiceController => {
    const [state, setState] = useState<LiveAiVoiceState>('idle');
    const [isMuted, setIsMuted] = useState(false);
    const [userCaption, setUserCaption] = useState('');
    const [assistantCaption, setAssistantCaption] = useState('');
    const [error, setError] = useState('');
    const peerRef = useRef<RTCPeerConnection | null>(null);
    const channelRef = useRef<RTCDataChannel | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const expiryTimerRef = useRef<number | null>(null);
    const voiceSessionIdRef = useRef<string | null>(null);
    const generationRef = useRef(0);
    const stateRef = useRef<LiveAiVoiceState>('idle');
    const mutedRef = useRef(false);
    const handledInputItemsRef = useRef<Set<string>>(new Set());
    const callbackRef = useRef(onUserTranscript);

    useEffect(() => {
        callbackRef.current = onUserTranscript;
    }, [onUserTranscript]);

    useEffect(() => {
        stateRef.current = state;
    }, [state]);

    useEffect(() => {
        mutedRef.current = isMuted;
    }, [isMuted]);

    const releaseResources = useCallback(() => {
        generationRef.current += 1;
        if (expiryTimerRef.current !== null) {
            window.clearTimeout(expiryTimerRef.current);
            expiryTimerRef.current = null;
        }

        streamRef.current?.getTracks().forEach(track => track.stop());
        streamRef.current = null;

        if (channelRef.current) {
            channelRef.current.onopen = null;
            channelRef.current.onmessage = null;
            channelRef.current.onclose = null;
            channelRef.current.onerror = null;
            if (channelRef.current.readyState !== 'closed')
                channelRef.current.close();
        }
        channelRef.current = null;

        if (peerRef.current) {
            peerRef.current.onconnectionstatechange = null;
            peerRef.current.ontrack = null;
            peerRef.current.close();
        }
        peerRef.current = null;

        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.srcObject = null;
            audioRef.current.remove();
        }
        audioRef.current = null;
        voiceSessionIdRef.current = null;
        handledInputItemsRef.current.clear();
        setIsMuted(false);
    }, []);

    const sendEvent = useCallback((event: object) => {
        if (channelRef.current?.readyState === 'open')
            channelRef.current.send(JSON.stringify(event));
    }, []);

    const speakAuthoritativeResponse = useCallback((text: string) => {
        const spokenText = text.trim();
        if (!spokenText)
            return;

        setAssistantCaption('');
        setState('speaking');
        sendEvent({
            type: 'response.create',
            response: {
                output_modalities: ['audio'],
                instructions: `Read the following response exactly as written. Do not add, remove, summarize, or reinterpret any words:\n\n${spokenText}`,
            },
        });
    }, [sendEvent]);

    const handleRealtimeEvent = useCallback(async (message: MessageEvent) => {
        let event: RealtimeEvent;
        try {
            event = JSON.parse(message.data);
        } catch {
            return;
        }

        switch (event.type) {
            case 'input_audio_buffer.speech_started':
                if (stateRef.current === 'speaking') {
                    sendEvent({ type: 'response.cancel' });
                }
                setAssistantCaption('');
                setUserCaption('Listening...');
                setState(mutedRef.current ? 'muted' : 'listening');
                break;
            case 'input_audio_buffer.speech_stopped':
                setState('thinking');
                break;
            case 'conversation.item.input_audio_transcription.delta':
                setUserCaption(current => event.delta ? `${current === 'Listening...' ? '' : current}${event.delta}` : current);
                break;
            case 'conversation.item.input_audio_transcription.completed': {
                const transcript = event.transcript?.trim() ?? '';
                const itemId = event.item_id || event.item?.id || transcript;
                if (!transcript || handledInputItemsRef.current.has(itemId))
                    break;

                handledInputItemsRef.current.add(itemId);
                setUserCaption(transcript);
                setState('thinking');
                streamRef.current?.getAudioTracks().forEach(track => {
                    track.enabled = false;
                });
                const turnGeneration = generationRef.current;
                try {
                    const voiceSessionId = voiceSessionIdRef.current;
                    if (!voiceSessionId)
                        throw new Error('The authorized voice session is no longer active.');
                    const assistantText = await callbackRef.current(transcript, voiceSessionId);
                    if (turnGeneration !== generationRef.current || channelRef.current?.readyState !== 'open')
                        break;
                    streamRef.current?.getAudioTracks().forEach(track => {
                        track.enabled = !mutedRef.current;
                    });
                    if (assistantText)
                        speakAuthoritativeResponse(assistantText);
                    else
                        setState(mutedRef.current ? 'muted' : 'listening');
                } catch {
                    if (turnGeneration !== generationRef.current)
                        break;
                    streamRef.current?.getAudioTracks().forEach(track => {
                        track.enabled = !mutedRef.current;
                    });
                    setError('Synergist AI could not process that voice turn. Your existing chat and draft are safe; try again or use text.');
                    setState(mutedRef.current ? 'muted' : 'listening');
                }
                break;
            }
            case 'response.output_audio_transcript.delta':
                setAssistantCaption(current => `${current}${event.delta ?? ''}`);
                setState('speaking');
                break;
            case 'response.output_audio_transcript.done':
                if (event.transcript)
                    setAssistantCaption(event.transcript);
                break;
            case 'response.done':
                setState(mutedRef.current ? 'muted' : 'listening');
                break;
            case 'error':
                setError('The realtime service reported an error. Reconnect or continue with text chat.');
                releaseResources();
                setState('error');
                break;
        }
    }, [releaseResources, sendEvent, speakAuthoritativeResponse]);

    const connect = useCallback(async (isReconnect: boolean) => {
        if (disabled)
            return;

        releaseResources();
        const generation = generationRef.current;
        setError('');
        setUserCaption('');
        setAssistantCaption('');
        setState(isReconnect ? 'reconnecting' : 'requesting-permission');

        try {
            if (!navigator.mediaDevices?.getUserMedia || typeof RTCPeerConnection === 'undefined') {
                setError('This browser does not support live voice. Text chat remains available.');
                setState('unavailable');
                return;
            }

            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            if (generation !== generationRef.current) {
                stream.getTracks().forEach(track => track.stop());
                return;
            }
            streamRef.current = stream;
            setState(isReconnect ? 'reconnecting' : 'connecting');

            const rawSession = await new AiVoiceClient().CreateSession({
                Workflow: workflow,
                ContextId: contextId || null,
            });
            if (generation !== generationRef.current)
                return;
            const session = normalizeVoiceSession(rawSession);
            if (!session.available || !session.sessionId || !session.clientSecret || !session.callsUrl) {
                releaseResources();
                setError(session.unavailableReason || 'Live voice is unavailable. Text chat remains available.');
                setState('unavailable');
                return;
            }
            voiceSessionIdRef.current = session.sessionId;

            const peer = new RTCPeerConnection();
            peerRef.current = peer;
            stream.getTracks().forEach(track => peer.addTrack(track, stream));

            const audio = document.createElement('audio');
            audio.autoplay = true;
            audio.setAttribute('aria-hidden', 'true');
            audio.style.display = 'none';
            document.body.appendChild(audio);
            audioRef.current = audio;
            peer.ontrack = event => {
                audio.srcObject = event.streams[0] ?? new MediaStream([event.track]);
                void audio.play().catch(() => {
                    setError('Audio playback was blocked. Use your browser audio control, reconnect, or continue with text.');
                });
            };

            const channel = peer.createDataChannel('oai-events');
            channelRef.current = channel;
            channel.onmessage = event => void handleRealtimeEvent(event);
            channel.onopen = () => setState('listening');
            channel.onerror = () => {
                setError('The voice data connection failed. Reconnect or continue with text chat.');
                releaseResources();
                setState('error');
            };
            peer.onconnectionstatechange = () => {
                if (peer.connectionState === 'failed' || peer.connectionState === 'disconnected' || peer.connectionState === 'closed') {
                    setError('The voice connection ended. Reconnect or continue with text chat.');
                    releaseResources();
                    setState('error');
                }
            };

            const offer = await peer.createOffer();
            if (generation !== generationRef.current)
                return;
            await peer.setLocalDescription(offer);
            if (generation !== generationRef.current)
                return;
            const providerResponse = await fetch(session.callsUrl, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${session.clientSecret}`,
                    'Content-Type': 'application/sdp',
                },
                body: offer.sdp,
            });
            if (generation !== generationRef.current)
                return;
            if (!providerResponse.ok)
                throw new Error(`Realtime connection failed (${providerResponse.status}).`);

            const answerSdp = await providerResponse.text();
            if (generation !== generationRef.current)
                return;
            await peer.setRemoteDescription({ type: 'answer', sdp: answerSdp });

            // The ephemeral credential expires after setup; the established call remains valid.
            expiryTimerRef.current = window.setTimeout(() => {
                setError('The voice session reached its time limit. Reconnect to continue, or use text chat.');
                releaseResources();
                setState('error');
            }, maxVoiceSessionMilliseconds);
        } catch (caught) {
            if (generation !== generationRef.current)
                return;
            const message = describeError(caught);
            releaseResources();
            setError(message);
            setState(caught instanceof DOMException && caught.name === 'NotFoundError' ? 'unavailable' : 'error');
        }
    }, [contextId, disabled, handleRealtimeEvent, releaseResources, workflow]);

    const start = useCallback(() => connect(false), [connect]);
    const reconnect = useCallback(() => connect(true), [connect]);

    const end = useCallback(() => {
        releaseResources();
        setUserCaption('');
        setAssistantCaption('');
        setError('');
        setState('ended');
    }, [releaseResources]);

    const toggleMute = useCallback(() => {
        const nextMuted = !isMuted;
        mutedRef.current = nextMuted;
        streamRef.current?.getAudioTracks().forEach(track => {
            track.enabled = stateRef.current === 'thinking' ? false : !nextMuted;
        });
        setIsMuted(nextMuted);
        if (stateRef.current !== 'thinking')
            setState(nextMuted ? 'muted' : 'listening');
    }, [isMuted]);

    const fallbackToText = useCallback(() => {
        releaseResources();
        setUserCaption('');
        setAssistantCaption('');
        setError('');
        setState('ended');
        onFallbackToText?.();
    }, [onFallbackToText, releaseResources]);

    useEffect(() => releaseResources, [releaseResources, workflow, contextId]);

    useEffect(() => {
        if (disabled && activeStates.includes(state))
            end();
    }, [disabled, end, state]);

    return {
        state,
        isMuted,
        isMicrophoneActive: streamRef.current?.getAudioTracks().some(track => track.readyState === 'live' && track.enabled) ?? false,
        userCaption,
        assistantCaption,
        error,
        start,
        toggleMute,
        end,
        reconnect,
        fallbackToText,
    };
};

export default useLiveAiVoice;
